import React, { useRef, useState } from "react";

import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
// No `ChevronRightIcon`: the collapsed chevron is `ChevronDownIcon` rotated
// -90deg, so the two states are one glyph that can travel between them.
import { ChevronDownIcon, CircleCheckBigIcon, InfoIcon } from "lucide-react";
import { Button, Card, FIELD_RADIUS } from "@repo/ui";
import { InfoTooltip } from "components";
import { DisclosureTextButton } from "components/input";
import { capitalize, centsToDollar } from "utils";
import {
  CORE_FEATURES_ROW,
  INCLUDED_ACTIVATIONS_ROW,
  TEAM_SEATS_ROW,
  PLAN_COMPARISON_SECTIONS,
  USAGE_SECTION_TITLE,
  formatSeatAllowanceCell,
  resolvePlanComparisonCell,
  resolvePlanDescription,
  resolvePlanTierKey,
  type PlanComparisonCellValue,
  type PlanComparisonRow,
  type PlanComparisonSection,
} from "constants/planComparison";

/**
 * Curated plan-comparison matrix. The row set itself lives in
 * `constants/planComparison` — the BillingOverview "More details" list renders
 * the SAME rows for the current tier, so they share one source (§36) rather than
 * two copies that drift.
 *
 * Rows are grouped into collapsible SECTIONS (Usage, Analytics, White-label,
 * Team & access) with a grey header band, a chevron accordion, and
 * expand/collapse-all — a flat list of this many rows is unscannable.
 *
 * Lookups key on `plan.tier_key`, NOT on the title. This is what VRG-365 fixed:
 * the catalog's titles came back "Vera Enterprise", the lookup lowercased them to
 * "vera enterprise", nothing matched, and every differentiating row rendered as
 * not-included on every tier — the matrix showed dashes all the way down while
 * looking perfectly healthy.
 */
type CellValue = PlanComparisonCellValue;
type ComparisonRow = PlanComparisonRow;
type Section = PlanComparisonSection;

/**
 * How far the selected column's tint runs PAST the table, top and bottom.
 *
 * Equal to the band's own corner radius, and that equality is the point: at
 * `overhang === radius` the curve is exactly consumed by the overhang, so the
 * band reads as a rounded cap wrapping the column rather than as a fill that
 * stops flush at the table's edge. Make the overhang smaller than the radius and
 * the curve gets cut off mid-arc by the first row; make it larger and the band
 * grows a straight-sided stub above the table.
 *
 * `FIELD_RADIUS` rather than a literal 8 because it is the app's INNER radius —
 * the one used for a control nested inside a 12px-radius container (see its own
 * note on concentric nesting). This band is nested inside the matrix `Card`,
 * whose radius is that same 12, so it is the same decision, not a coincidence.
 *
 * It has to stay <= the Card's 24px padding, since that padding is the only room
 * the band has to grow into. At 8 it clears with 16px to spare on both surfaces.
 */
const BAND_OVERHANG = FIELD_RADIUS;

/**
 * Section accordion motion.
 *
 * Both values are LIFTED from `@repo/ui`'s `useAnimatedHeight` rather than
 * picked. That hook is the app's existing answer to "a container is changing
 * size", and its easing note applies here verbatim — decelerating, no
 * overshoot, because overshoot on a container reads as instability rather than
 * polish. A section opening in this table and a modal resizing elsewhere now
 * travel on the same curve at the same speed.
 *
 * The hook ITSELF is deliberately not used here: it is ResizeObserver-driven
 * and self-skips while the element is hidden, and it animates one measured
 * element rather than a row of a grid. The technique below needs no
 * measurement at all — see the wrapper in the render.
 */
const SECTION_MOTION_MS = 260;
const SECTION_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * The chevron is ONE glyph that rotates, not two glyphs that swap.
 *
 * It used to be a `ChevronRightIcon`/`ChevronDownIcon` conditional, which
 * snapped instantly while the rows beneath it took 260ms to travel — the
 * control finished before the thing it controls had started, which is the same
 * "flash" the height animation exists to remove. `chevron-down` rotated -90°
 * IS `chevron-right`, so the state convention is unchanged (down = expanded,
 * right = collapsed); only the transition between them is.
 *
 * Applied to the section headers AND to expand/collapse-all, which carries the
 * same chevron in the same convention on purpose — animating one and not the
 * other would break the "master control" reading they share.
 */
const chevronSx = (isCollapsed: boolean) => ({
  transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
  transition: `transform ${SECTION_MOTION_MS}ms ${SECTION_EASING}`,
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

/**
 * The annual "Save $X" line, which grows in and collapses out instead of appearing
 * and vanishing at full height.
 *
 * ⚠️ THE RETAINED TEXT IS THE WHOLE TRICK, and it is not an optimisation. `1fr`
 * resolves against the row's CONTENT, so if the text left on the same frame the
 * track started shrinking there would be nothing to shrink FROM — the row would
 * snap to zero and the transition would animate an already-collapsed box. Holding
 * the last real string gives the collapse something to measure against.
 *
 * A component rather than inline JSX because that retention needs a ref that
 * survives the toggle, and hooks cannot live inside the column `.map`.
 *
 * `aria-hidden` while closed: the row is clipped to a zero-height track rather than
 * unmounted, so without it a screen reader would still read out a stale "Save $589"
 * on the monthly view — the same reasoning the collapsible sections below use.
 */
const SavingsRow = ({
  label,
  greenText,
}: {
  label: string | null;
  greenText: string;
}) => {
  const retained = useRef(label);
  if (label) retained.current = label;
  const open = Boolean(label);

  return (
    <Box
      aria-hidden={!open}
      /* ⚠️ The animated values are INLINE, not in `sx`, and that is load-bearing.
         Measured on this exact node: when the two states live in `sx`, emotion swaps
         the generated class and Chrome starts a transition for `opacity` but NOT for
         `grid-template-rows` — so the row snapped shut while the text faded, which
         looked like no animation at all. Changing the same property inline on the same
         node fires a `CSSTransition` every time. Both are driven inline so they cannot
         drift apart again. The declaration itself stays in `sx`; only the changing
         values moved. */
      style={{
        gridTemplateRows: open ? "1fr" : "0fr",
        opacity: open ? 1 : 0,
      }}
      sx={{
        display: "grid",
        transition: `grid-template-rows ${SECTION_MOTION_MS}ms ${SECTION_EASING}, opacity ${SECTION_MOTION_MS}ms ${SECTION_EASING}`,
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      {/* `minHeight: 0` is load-bearing: a grid item defaults to `min-height: auto`
          and refuses to shrink below its content, so without it the 0fr track never
          collapses and the animation plays with nothing moving. */}
      <Box sx={{ overflow: "hidden", minHeight: 0 }}>
        <Typography
          variant="body3"
          sx={{ mt: 1, fontWeight: 600, color: greenText }}
        >
          {retained.current}
        </Typography>
      </Box>
    </Box>
  );
};

/** A plan column's call-to-action, computed by the page (it owns subscription
 * state, current-plan comparison, and navigation) and rendered by this matrix. */
export interface PlanColumnAction {
  label: string;
  variant: "primary" | "secondary";
  disabled: boolean;
  onClick: () => void;
}

/**
 * The minimum a plan must carry to be a column here — deliberately STRUCTURAL
 * rather than `SubscriptionPlan`, because two different catalogs feed this
 * component (VRG-383):
 *
 *  - the AUTHENTICATED `GET /subscriptions/get-all-plans` (`SubscriptionPlan`),
 *    read by the billing plan-change page, and
 *  - the PUBLIC `GET /signup/plans` (`SignupPlan`), read by the self-serve
 *    signup page's plan step, which has no internal product id and adapts
 *    its camelCase payload to these names at the call site.
 *
 * The component is generic over the concrete type so `planAction` /
 * `planSavingsLabel` / `planSubline` receive the caller's OWN plan object back,
 * not this narrowed view — the billing page's callbacks need `stripe_price_id`,
 * which does not belong in a shape signup can satisfy.
 */
export interface PlanColumnPlan {
  /** Stable per-column key. Billing uses the catalog row id; signup the price id. */
  id: string;
  title: string;
  description?: string | null;
  /** Stripe `unit_amount`-shaped — always cents. */
  amount: number;
  interval: string;
  included_activations?: number | null;
  /** See `resolvePlanTierKey` — every per-tier row is keyed on this. */
  tier_key?: string | null;
}

interface PlanComparisonMatrixProps<T extends PlanColumnPlan> {
  plans: T[];
  // Match by tier TITLE (not id): the annual view is the same tier at a different
  // price/SKU, so the current column should stay highlighted on both intervals.
  // Title is still the right key HERE — this is a same-catalog title-to-title
  // comparison between two rows that came from the same sync, not a lookup into
  // a hardcoded table, so a "Vera " prefix appears on both sides or neither.
  currentPlanTitle?: string | null;
  /** Per-column CTA (Subscribe / Upgrade / Downgrade / Current plan / renewal text). */
  planAction: (plan: T) => PlanColumnAction;
  /**
   * Whether clicking anywhere in a plan COLUMN runs that column's action, or only
   * its button does. Defaults to FALSE — the button is the commit path.
   *
   * Opt-in because the two callers are not the same interaction (§34's axis, in one
   * component):
   *
   *  - SIGNUP is a picker. The action SELECTS a plan, costs nothing, and is undone
   *    by picking another, so a column-sized target is a convenience and it passes
   *    `columnClickable`.
   *  - BILLING is an action row. The action navigates into CHECKOUT, so the column
   *    is not a target at all and this stays off.
   *
   * Why off by DEFAULT rather than on: the matrix is a reading surface — you drag
   * the pointer across it to compare tiers — and defaulting to clickable makes the
   * scanning surface and the commit surface the same pixels. That is tolerable for
   * a reversible pick and wrong for a billing flow, so the risky case has to ask.
   *
   * Note the button is already the only KEYBOARD-reachable path (the cells are divs
   * with onClick, not buttons), so this removes a mouse-only duplicate rather than
   * an affordance anyone else had.
   */
  columnClickable?: boolean;
  /** Optional green "Save $X" label shown beside the price (annual savings). */
  planSavingsLabel?: (plan: T) => string | null;
  /**
   * Optional per-column line between the price and the plan description — the
   * signup step's "Pays for itself. N included activations cover the base."
   * (VRG-383). A render slot, like `footer` and `intervalControl`: the sentence
   * is an acquisition argument the page owns, not something a comparison table
   * should be composing for itself.
   *
   * OPTIONAL, and the billing page passes nothing, so that surface is unchanged
   * — the reserved row below exists only when this prop is supplied.
   *
   * The height it renders in is FIXED and the text clamped, for the same reason
   * `plan.description` below it is: a column that renders three lines where its
   * neighbour renders two pushes its CTA out of line with the rest of the row.
   */
  planSubline?: (plan: T) => React.ReactNode;
  /**
   * The Monthly/Annual control, rendered in the label column's header cell.
   *
   * A slot rather than internal state because the interval decides which plans are
   * even passed in (`filteredPlans`), so the page has to own it.
   *
   * It sits HERE rather than in the page header because it changes the prices in
   * the columns immediately to its right — the same "put it on the row it acts on"
   * argument as the current-plan summary below it. In the page header it was
   * top-right, far from the numbers it changes, and easy to miss entirely.
   */
  intervalControl?: React.ReactNode;
  /**
   * Rendered as the last thing inside the card, below the final section.
   *
   * A slot, not a hardcoded link to the contact-sales card, so this component keeps
   * knowing only about the plans it was given. The page owns what sits below it and
   * therefore owns what to point at.
   */
  footer?: React.ReactNode;
  /**
   * Start every section EXCEPT the first collapsed. Defaults to false, so the
   * billing plan-change page keeps its fully-expanded matrix unchanged.
   *
   * Signup sets it: there the matrix is the last step of a form rather than a
   * destination, so ~20 open rows sit between the plan just chosen and the
   * button that continues.
   */
  collapseSectionsByDefault?: boolean;
  /**
   * Render row tooltips WITHOUT their definition footer link.
   *
   * Set by the signup step, which is public: the footer points into `/docs`,
   * which an unauthenticated visitor cannot reach, and dangling a link out of a
   * half-finished signup form is a poor trade even when it resolves. The
   * tooltip BODY still comes from the glossary either way (§44).
   */
  publicSurface?: boolean;
  /**
   * Render "Compare plans" + the interval control CENTRED above the grid,
   * instead of in the grid's own top-left cell.
   *
   * Defaults to false, so the billing plan-change page keeps the corner-cell
   * layout — there the toggle sitting beside the prices it changes is the whole
   * argument for that placement, and the page is a destination the visitor
   * navigated to deliberately.
   *
   * Signup sets it: that step is the end of a form, the corner cell was
   * carrying three unrelated controls, and a left-pinned heading over four
   * columns read as belonging to the first column rather than the card.
   */
  centeredHeading?: boolean;
}

// Generic in the plan type (see `PlanColumnPlan`). The trailing comma in the
// type parameter list is required in a .tsx file — without it `<T extends …>`
// parses as JSX.
export const PlanComparisonMatrix = <T extends PlanColumnPlan,>({
  plans,
  currentPlanTitle,
  planAction,
  columnClickable = false,
  planSavingsLabel,
  planSubline,
  intervalControl,
  footer,
  collapseSectionsByDefault = false,
  publicSurface = false,
  centeredHeading = false,
}: PlanComparisonMatrixProps<T>) => {
  const theme = useTheme();
  // Shared "savings/positive" green (matches BillingPlanChangePage's own
  // greenText — the green scale isn't inverted for dark mode).
  const greenText =
    theme.palette.mode === "dark"
      ? theme.palette.green[300]
      : theme.palette.green[700];
  // Data-driven activations row, injected at the top of the Usage section. Keyed
  // by tier_key like every other row, so a "Vera "-prefixed title can't orphan it.
  const activationsRow: ComparisonRow = {
    ...INCLUDED_ACTIVATIONS_ROW,
    byTier: Object.fromEntries(
      plans.flatMap((plan) => {
        const tierKey = resolvePlanTierKey(plan);
        return tierKey
          ? [[tierKey, String(plan.included_activations ?? 0)] as const]
          : [];
      }),
    ),
  };
  // VRG-438 — same treatment for seats, and for a sharper reason: this row used to
  // carry a hardcoded 1 / 1 / 5 / Unlimited against an API that counted and limited
  // nothing. It now reads `plan.included_seats`, the column the invite path refuses
  // against, so the matrix cannot advertise a figure the API does not honour.
  const seatsRow: ComparisonRow = {
    ...TEAM_SEATS_ROW,
    byTier: Object.fromEntries(
      plans.flatMap((plan) => {
        const tierKey = resolvePlanTierKey(plan);
        return tierKey
          ? [[tierKey, formatSeatAllowanceCell(plan.included_seats)] as const]
          : [];
      }),
    ),
  };
  const sections: Section[] = PLAN_COMPARISON_SECTIONS.map((section) =>
    section.title === USAGE_SECTION_TITLE
      ? { ...section, rows: [activationsRow, seatsRow, ...section.rows] }
      : section,
  );

  const allTitles = sections.map((s) => s.title);
  /**
   * VERA-1095: opt-in. Everything open is right on the billing page, where the
   * visitor navigated deliberately to compare, and it is the DEFAULT here so
   * that page is untouched.
   *
   * Signup opts in, because the same matrix does a different job there: it sits
   * at the end of a form, in front of someone who wants to finish. Fully
   * expanded it ran 2,166px — 1.66 screens — and put ~20 rows between the plan
   * they just picked and the button that continues.
   *
   * The FIRST section stays open rather than collapsing everything: Usage &
   * limits holds the rows people actually choose on (activations, AI queries,
   * seats), and an all-collapsed matrix would hide the comparison the step
   * exists for behind four more clicks. The rest are for visitors who already
   * know they need them.
   */
  const [collapsed, setCollapsed] = useState<Set<string>>(() =>
    collapseSectionsByDefault ? new Set(allTitles.slice(1)) : new Set(),
  );

  /**
   * THE WHOLE COLUMN IS THE CLICK TARGET (VERA-1095), not just its CTA button.
   *
   * The hard part is that a "column" is not a DOM element. This is a CSS grid
   * whose ROWS are the elements — one `display: grid` box per row, each holding
   * a label cell plus one cell per plan — so a column is ~30 separate boxes
   * scattered across ~30 different parents. That is already why the selected
   * column's tint is drawn as ONE absolutely-positioned band behind the table
   * rather than as a fill on every cell (see the band below).
   *
   * The band cannot be reused as the hit target, and not merely because it
   * carries `pointerEvents: none`: it sits at `zIndex: -1`, so every cell of
   * every row paints ON TOP of it. Hit-testing goes to the topmost element under
   * the pointer whether or not that element has a handler, so a target beneath
   * the rows can never receive a click. Raising it above them inverts the
   * problem — it would then swallow the Select button and the row tooltips.
   *
   * So the click target is composed the same way the column itself is: each
   * plan CELL carries the handler (`planCellProps`), which is three call sites
   * because the cells are produced by three maps (header, section header, data
   * row). The alternative considered and rejected was one overlay grid with
   * `pointerEvents: auto` per column plus per-child re-enabling — same number of
   * touch points, but it would have to sit above the content and then punch
   * holes back out for every interactive child, and a child added later would
   * silently become unclickable.
   *
   * HOVER has the identical problem: `:hover` on a cell only knows about that
   * cell, so the column's hover tint has to be React state read by the band.
   * (The file's own history notes a hover-linked tint that existed for a
   * different reason and was removed; this is not that — it links a column to
   * itself, not to a card that used to sit above it.)
   *
   * §34's selectable-card axis decides what the two states LOOK like: hover is a
   * tint only, never a border, because the border is what says "chosen" and
   * reusing it for hover would collapse the two states into one.
   */
  const [hoveredPlanId, setHoveredPlanId] = useState<string | null>(null);
  /**
   * KEYBOARD, and the honest answer to what a column can be semantically.
   *
   * A column cannot be a `<button>` or a `role="radio"`: both are leaf roles, and
   * this one already CONTAINS the Select button — nesting an interactive element
   * inside either is invalid, breaks the accessible name (the whole column gets
   * flattened into the control's label) and produces two tab stops that do the
   * same thing. A visually-hidden `<input type="radio">` per column was the next
   * candidate and is worse on the billing surface specifically: native radio
   * groups CHECK on arrow-key, and "checking" there means navigating to
   * checkout, so simply browsing the group with the keyboard would fire the
   * action.
   *
   * The keyboard control for a column is therefore the control that already is
   * one — its CTA button, which every column has, which is labelled with the
   * action, and which is one tab stop. What was missing is that focusing it said
   * nothing about WHICH column it belonged to (four buttons all reading
   * "Select"), so focus now lights the column exactly as hover does, and the
   * button carries an `aria-label` naming its plan. Tab reaches a column,
   * Enter/Space activates it, and the column is visibly the thing being acted
   * on — with no ARIA claim that isn't true.
   *
   * The plan cells themselves stay plain, non-focusable divs: they are a pointer
   * affordance duplicating a control that is already reachable, so giving each
   * of ~30 cells its own tab stop would be noise, not access.
   */
  const [focusedPlanId, setFocusedPlanId] = useState<string | null>(null);
  const allCollapsed = collapsed.size === allTitles.length;
  const toggleAll = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(allTitles));
  const toggleSection = (title: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });

  if (plans.length === 0) return null;

  // First column is the feature label; one column per plan after it.
  /**
   * `minmax(0, 1fr)` on the plan columns, not a bare `1fr`. A bare `1fr` is
   * `minmax(auto, 1fr)`, and `auto` floors at MIN-CONTENT — so the widest price
   * in a column sets a floor that column can never go below.
   *
   * That is what made switching Monthly -> Annual shift the whole matrix left:
   * "$9,999" has a far wider min-content than "$999", the plan columns grew to
   * fit it, and the only flexible track left to give up the space was the label
   * column, which collapsed toward its 220px minimum and wrapped the "Save N%"
   * chip onto a second line under the interval toggle. Nothing about the layout
   * intent changed between the two intervals — only the digit count.
   *
   * With a 0 minimum the plan columns divide the row evenly regardless of what
   * they contain, so the interval toggle no longer reflows the page it lives in.
   * Same fix, same reasoning, as the signup step track.
   */
  const gridTemplateColumns = `minmax(220px, 1.6fr) repeat(${plans.length}, minmax(0, 1fr))`;

  const isCurrentColumn = (plan: PlanColumnPlan) =>
    !!currentPlanTitle &&
    plan.title.toLowerCase() === currentPlanTitle.toLowerCase();

  // Current-column tint. This used to be shared with a hover tint that linked a plan
  // CARD above to its column here (the cards sat on a full-width Grid with no leading
  // label column, so a card was never physically above its own column and the tint was
  // what said "same plan"). The plan header now renders INSIDE the column itself, so
  // there is no second surface to link and no shear to compensate for — the
  // hover-linkage machinery is gone.
  const columnTint = alpha(theme.palette.brand[500], 0.08);

  /**
   * The hover/focus tint — §34's canonical hover alpha, and NO border.
   *
   * §34's pair is 0.06 hover against 0.1 selected. Selected is 0.08 here and is
   * left alone (its own note above argues that number from the measured contrast
   * of the surface it composites onto), which leaves hover only two points below
   * it — and that is fine, because the two states are NOT distinguished by the
   * tint delta. The BORDER is what says "chosen"; the note on `columnEdge`
   * records that the 8% wash measures ~1.09:1 and was never carrying the
   * selection on its own. So hover can take the full 0.06 without blurring into
   * selected.
   *
   * It was briefly 0.04, to buy a 2x separation from selected that nothing
   * needed. Measured on the light signup card that composites to roughly
   * #faf8fe on #ffffff — invisible, which fails the one job a hover state has.
   *
   * No border, deliberately: a hover border would say the column is already
   * chosen, which is the collision §34 exists to prevent.
   */
  const columnHoverTint = alpha(theme.palette.brand[500], 0.06);

  /**
   * The selected column takes a BORDER as well as the tint, and the border is
   * the part that says "chosen".
   *
   * §34 states the axis: tint = considering, border = chosen. The band had only
   * the tint, so the strongest statement on the page — this is the plan you are
   * buying — was carried by an 8% wash. Measured against the light surface it
   * composites to roughly #f4f0fe against #ffffff, about 1.09:1: technically
   * present, and below the 3:1 a graphical boundary answers to. It reads as a
   * faint stripe rather than as a selection, which is what prompted this.
   *
   * `brand[500]` at full strength rather than another alpha, because a border's
   * whole advantage over a fill here is that 1px of a definite colour does not
   * depend on how much headroom the surface behind it has — the same argument
   * `authFieldBorder` records for the auth fields on their tinted page. It is
   * also why this works unchanged in BOTH modes while the tint does not: dark's
   * paper is near-black, so an 8% purple wash on it has almost nothing to
   * separate from, and the edge is doing nearly all the work there.
   *
   * Drawn on the band, not on the cells. The band is one element spanning the
   * whole column, so the border is a single unbroken rectangle with the same
   * rounded cap as the tint; put on the cells it would be a stack of ~30
   * separate boxes with a horizontal rule between every row.
   */
  const columnEdge = `1px solid ${theme.palette.brand[500]}`;

  /**
   * Every column's CTA, resolved ONCE per render.
   *
   * `planAction` is a caller callback and the billing page's implementation is
   * not cheap (it re-derives current-plan / upgrade-direction state per call).
   * The column click reads it from every one of ~30 cells per column, so calling
   * it inline would turn one call per column into ~120.
   */
  const actionByPlanId = new Map(
    plans.map((plan) => [plan.id, planAction(plan)] as const),
  );

  /** Hovered or keyboard-focused — the column the pointer/caret is "on". */
  const isActiveColumn = (plan: PlanColumnPlan) =>
    hoveredPlanId === plan.id || focusedPlanId === plan.id;

  const columnBg = (plan: PlanColumnPlan) =>
    isCurrentColumn(plan)
      ? columnTint
      : isActiveColumn(plan)
      ? columnHoverTint
      : undefined;

  const columnBorder = (plan: PlanColumnPlan) =>
    isCurrentColumn(plan) ? columnEdge : "1px solid transparent";

  /**
   * What makes one plan cell part of the column's click + hover target.
   *
   * Spread onto every plan cell in all three maps. `onClick` runs the column's
   * OWN action — the same thing its button does — rather than a separate
   * "select" callback, so the two can never disagree and no caller has to opt
   * in: signup's action selects the plan, billing's opens that plan's checkout.
   * A disabled action (billing's current-plan column) yields no handler and no
   * pointer cursor, so the column is inert exactly when its button is.
   */
  const planCellProps = (plan: T) => {
    const action = actionByPlanId.get(plan.id);
    // `columnClickable` gates the CLICK, the action's own `disabled` gates it again:
    // a column is a target only when the caller opted in AND its button is live, so
    // the column can never do something the button would refuse.
    const clickable = columnClickable && !!action && !action.disabled;
    return {
      onClick: clickable ? () => action.onClick() : undefined,
      // Hover tracking stays on REGARDLESS. It is not an affordance here — in a
      // matrix this wide the tint is what keeps your eye in the right column while
      // reading across a feature row, which is just as useful when nothing is
      // clickable. Only the pointer cursor promises a click, so only it is gated.
      onMouseEnter: () => setHoveredPlanId(plan.id),
      onMouseLeave: () =>
        setHoveredPlanId((current) => (current === plan.id ? null : current)),
      cursor: clickable ? "pointer" : "default",
    };
  };

  const renderCell = (value: CellValue) => {
    if (value === true) {
      return (
        <CircleCheckBigIcon
          size={18}
          strokeWidth={1.5}
          color={theme.palette.grey[500]}
        />
      );
    }
    if (value === false) {
      return (
        <Typography variant="body3" color="text.secondary">
          —
        </Typography>
      );
    }
    return (
      <Typography variant="body3" color="text.primary">
        {value}
      </Typography>
    );
  };

  // Reusable label cell (feature name + optional info icon).
  //
  // The whole label is the hover target, not the 14px dot — a tooltip hung off
  // the icon alone is one most readers never find, because the thing they are
  // looking at when they get confused is the WORDS. `term` resolves against the
  // glossary so the body is the single definition rather than a retyped copy,
  // and the reader gets a link to the full entry (§44).
  const labelCell = (
    label: string,
    tip?: string,
    primary = false,
    term?: string,
    isLast = false,
  ) => {
    const content = (
      <Box
        sx={{
          pt: 3,
          // The last row carries extra space beneath it so the current column's
          // tint can run PAST its content before rounding off, instead of stopping
          // dead on the final divider. Applied to the label cell as well as the
          // plan cells: the row centres its children, so padding only the tinted
          // cell would drop this label below the ticks it labels.
          pb: isLast ? 8 : 3,
          pr: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          ...(tip || term ? { cursor: "help" } : null),
        }}
      >
        <Typography
          variant="body3"
          color={primary ? "text.primary" : "text.secondary"}
          fontWeight={primary ? 500 : 400}
        >
          {label}
        </Typography>
        {(tip || term) && (
          <InfoIcon size={14} strokeWidth={1.5} color={theme.palette.grey[500]} />
        )}
      </Box>
    );
    return tip || term ? (
      <InfoTooltip term={term} text={tip} hideDefinitionLink={publicSurface}>
        {content}
      </InfoTooltip>
    ) : (
      content
    );
  };

  const dataRow = (
    row: ComparisonRow,
    keyPrefix: string,
    primary = false,
    isLast = false,
  ) => (
    <Box
      key={`${keyPrefix}-${row.label}`}
      sx={{
        display: "grid",
        gridTemplateColumns,
        alignItems: "center",
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* The label cell is deliberately NOT part of any column's target: it is
          the row's own header, it belongs to no plan, and it is the tooltip
          trigger — clicking a definition must never buy a plan. It sits in the
          first grid track, which no plan cell occupies, so this needs no
          `stopPropagation`; the handlers simply are not on it. */}
      {labelCell(row.label, row.tooltip, primary, row.term, isLast)}
      {plans.map((plan) => {
        const { cursor, ...hit } = planCellProps(plan);
        return (
        <Box
          key={`${row.label}-${plan.id}`}
          {...hit}
          sx={{
            cursor,
            pt: 3,
            // Extra bottom space on the last row — see `labelCell`. The tint band
            // behind the table runs into this, so it ends below the content with room
            // to breathe rather than crowding the final divider.
            //
            // The tint itself is NOT painted here (nor is the bottom corner rounded):
            // it is one absolutely-positioned band behind the whole table — see
            // `BAND_OVERHANG` and the overlay in the render below.
            pb: isLast ? 8 : 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {renderCell(
            primary ? true : resolvePlanComparisonCell(row, resolvePlanTierKey(plan)),
          )}
        </Box>
        );
      })}
    </Box>
  );

  return (
    // No top margin: `mt: 8` (32px) lived here to clear the row of plan cards that
    // used to sit above. Those are gone and this is now the page's first content, so
    // the shared `Header` owns that gap on its own.
    <Card>
      {/* Header row: blank label cell + a full plan "card" per column — title,
          a reserved-height Current-plan chip slot, price, positioning copy, and
          the CTA. This replaces the separate row of plan cards above the matrix
          (the model Vergence's own in-app upgrade page uses): a card was never
          physically above its own column (see the module doc comment above), so
          folding the card INTO the column removes the misalignment instead of
          compensating for it with a hover-linked tint. */}
      {/* Heading + interval control, CENTRED above the grid.

          They used to live in the grid's own top-left cell, on the argument that
          the toggle belongs beside the prices it changes. That argument is real
          but it lost to two others: the cell had to hold a heading, a toggle
          carrying a "Save N%" chip, and the collapse-all, which is three
          unrelated controls in one ~300px corner; and a left-pinned heading over
          a four-column grid reads as belonging to the first column rather than
          to the whole card.

          Centred, "Compare plans" labels the whole matrix and the toggle sits
          directly above the price row it drives — losing horizontal proximity
          but gaining vertical, which is the shorter distance here anyway.

          Applies to BOTH surfaces: this component is shared with the billing
          plan-change page. */}
      {centeredHeading && intervalControl && (
        /* Interval control TOP-RIGHT, and no heading at all.

           `&& intervalControl` because the band is nothing BUT the control:
           without the guard, a `centeredHeading` caller that passes no control
           (which signup now is — its toggle moved up into the page's
           step-indicator row) renders an empty Stack whose `pb: 5` is 20px of
           dead space above the table. Not reachable from the billing
           plan-change page, which does not set `centeredHeading` at all and
           takes the corner-cell branch below.

           "Compare plans" was cut rather than moved: on signup the page already
           says "Choose your plan" three lines above, so the card was labelling
           itself a second time with a near-synonym. On billing the heading still
           earns its place (the page title there is "Change Plan" and the card is
           one section among several), which is why this is behind the prop.

           Top-right is the conventional home for an interval switch and it is
           where the eye goes for a global control. It loses the horizontal
           adjacency to the prices that the corner-cell placement had — but with
           the heading gone there is nothing else competing in this band, so the
           control reads immediately instead of being the second thing in a
           stack. */
        <Stack direction="row" justifyContent="flex-end" sx={{ pb: 5 }}>
          {intervalControl}
        </Stack>
      )}
      {/* The TABLE, and the containing block for the selected column's tint band.
          Everything from the plan-header row to the last section lives in here; the
          heading above and the `footer` below deliberately do not, because the band
          is sized to this box and should not run past either of them.

          `isolation: isolate` makes this a stacking context, which is what lets the
          band sit at `zIndex: -1` — behind every row's own background and border, but
          still ABOVE the Card's paper. Without the isolate, a negative z-index would
          escape to the nearest ancestor context and paint behind the card entirely,
          i.e. vanish. */}
      <Box sx={{ position: "relative", isolation: "isolate" }}>
        {/* The selected column's tint, as ONE band behind the whole table rather than
            a fill repeated on every cell.

            It is the same grid template as every row, so the band lands on its column
            with no measurement — no ResizeObserver, no reading track sizes back out of
            the layout, and it stays correct through a Monthly/Annual switch that
            changes every column's content width.

            Absolutely positioned, so it contributes NOTHING to layout: the rows sit
            exactly where they did before the band existed, which is the property that
            made this preferable to giving the first and last cells a negative margin
            and matching padding. It also means the band does not care which element
            happens to END the table — the last data row when the final section is
            open, that section's grey header when it is collapsed — so the rounded cap
            survives every combination of expand/collapse instead of being pinned to
            one of them.

            First cell is the label column and is deliberately left blank: the band
            wraps a PLAN column and must never tint the row labels.

            It draws the HOVER/FOCUS tint too (VERA-1095), for the same reason it
            draws the selected one — a column has no element of its own to hang a
            `:hover` rule on, so the state lives in React and is painted here, once,
            spanning the whole column. `pointerEvents: none` stays: this paints the
            state, the cells detect it. */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: `-${BAND_OVERHANG}px`,
            bottom: `-${BAND_OVERHANG}px`,
            left: 0,
            right: 0,
            display: "grid",
            gridTemplateColumns,
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <Box />
          {plans.map((plan) => (
            <Box
              key={`band-${plan.id}`}
              sx={{
                backgroundColor: columnBg(plan),
                // Transparent on the unselected columns rather than absent, so
                // the band's box is the same size in both states and selecting a
                // plan cannot nudge the column by a pixel. `box-sizing:
                // border-box` is global (normalize.ts), so the edge draws inside
                // the grid track either way and the rows never move.
                border: columnBorder(plan),
                // A string, NOT the bare 8 the per-cell corners used: `borderRadius`
                // is one of the props MUI's sx multiplies by `theme.shape.borderRadius`
                // (4 here), so a numeric 8 would silently render 32px. The longhand
                // `borderTopLeftRadius` this replaces is not in that style set, which
                // is why the old bare number was right there and is wrong here.
                borderRadius: `${FIELD_RADIUS}px`,
                // Kept from the cells this replaces. On signup the selection moves
                // between columns as the visitor clicks a different plan's CTA, and
                // the cross-fade is what makes that read as the band moving rather
                // than as the table repainting.
                transition: "background-color 0.12s ease",
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns, alignItems: "stretch" }}>
          {/* The label column's header cell, carrying BOTH the card's title and
              "you are here".

              `space-between` is what fills it. The card's "Compare plans" heading used
              to sit in its own row above the grid, which left this cell empty except
              for a short line pinned to the bottom — a ~190px box holding ~40px of
              text, which read as fine print in a void. Moving the heading in here tops
              and tails the cell with real content and removes a whole row above the
              grid at the same time.

              The summary stays BOTTOM-aligned so it lands on the CTA baseline
              (measured: both at 423), which is what makes it read as the left end of
              the plan row rather than as stray text. The heading tops out level with
              the plan titles.

              This is also the only place the "Current" chip now appears. It used to
              sit in a reserved-height slot in every column, which cost a fiddly
              baseline fix and stated a third time what the tint and the disabled
              "Current plan" button already said. Naming the tier HERE covers the one
              case those two do not: on the Annual view the button reads "Switch to
              annual", so without this the tint would be the only signal — and a
              tinted column conventionally means RECOMMENDED, not current. */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              // Heading + interval control, top-aligned, and nothing below them.
              //
              // The current-plan summary used to sit at the bottom of this cell, put
              // there to fill its height. It read wrong: a comparison table's corner
              // cell describing YOUR subscription is page-level context in a
              // component-level slot. It moved back under the page title, and the
              // interval control — which genuinely belongs beside the prices it
              // changes — is what fills the cell now.
              //
              // The space beneath is deliberate. A matrix's top-left corner is
              // conventionally empty; what looked wrong before was a lone short line
              // pinned to the bottom of it, reading as fine print, not the emptiness.
              justifyContent: "space-between",
              // Matches the plan columns' own `pt: 5`, so the heading sits on the plan
              // titles' line and the header reads as one row.
              pt: 5,
              pb: 4,
              pr: 4,
            }}
          >
            {/* When `centeredHeading` is set the heading and interval control
                render above the grid instead (see there) and this corner holds
                only the collapse-all, which is close to the conventional empty
                top-left of a matrix. Otherwise they live here, beside the prices
                the toggle changes. */}
            {centeredHeading ? (
              <Box />
            ) : (
              <Stack spacing={5}>
                <Typography variant="body1" color="text.primary">
                  Compare plans
                </Typography>
                {intervalControl}
              </Stack>
            )}
            {/* Expand/collapse-all, sitting on the CTA baseline so it reads as the
                left end of that row — the same placement argument as everything else
                in this cell. It was briefly a bordered row of its own between the
                header and the first data row, which read as a table row that happened
                to contain a link, which is the one thing it is not.

                It carries the SAME chevron as the section headers, in the same state
                convention (down = expanded, right = collapsed), so it reads as their
                master control rather than as unrelated text. */}
            {/* This control IS `DisclosureTextButton` — the component was extracted FROM
                here when the ROI hero needed the same treatment (§36), so what changed is
                where the markup lives, not how it renders. Two behavioural gains came with
                the move: it is now a real `<button>` (this was a `Stack` with an `onClick`,
                unreachable by keyboard and announcing nothing) and it carries
                `aria-expanded`. Its chevron rotates on the same curve as the section
                chevrons it commands, in the same convention. */}
            {/* Both strings rather than a ternary, so the button reserves the wider label
                and its width stops changing when pressed. Harmless here today — it is the
                leading item in its cell, so only its trailing edge moved — but it is the
                same latent defect that walked the ROI hero's KPI figures sideways, and the
                component now takes the pair precisely so no call site has to notice. */}
            <DisclosureTextButton
              expanded={!allCollapsed}
              onToggle={toggleAll}
              label={{ expanded: "Collapse all", collapsed: "Expand all" }}
            />
          </Box>
          {plans.map((plan) => {
            const action = actionByPlanId.get(plan.id)!;
            const savingsLabel = planSavingsLabel?.(plan) ?? null;
            const { cursor, ...hit } = planCellProps(plan);
            return (
              <Box
                /* Keyed on the TIER, not the plan id — and this is what makes the
                   savings row animate at all.

                   `filteredPlans` swaps the monthly catalog rows for the annual ones,
                   which are DIFFERENT rows with different ids, so a `plan.id` key
                   remounted this whole column on every toggle. A remounted node has no
                   before-state, so a CSS transition inside it can never fire and
                   `SavingsRow`'s retained text was wiped on the way out. The tier is
                   the thing a reader perceives as continuous across the toggle
                   ("Business" is still Business at a different price), so keying on it
                   both matches the mental model and keeps the node alive.

                   Falls back to `plan.id` when a plan has no resolvable tier: such a
                   column simply remounts as before, losing the animation rather than
                   colliding with a sibling's key. Tiers are unique within one render —
                   `filteredPlans` yields exactly one plan per tier per interval. */
                key={`head-${resolvePlanTierKey(plan) ?? plan.id}`}
                {...hit}
                sx={{
                  cursor,
                  textAlign: "center",
                  pt: 5,
                  pb: 4,
                  px: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // No tint and no top rounding here. Both belong to the single band
                  // behind the table (see `BAND_OVERHANG` and the overlay in the
                  // render below) — painting them per-cell as well would double the
                  // 8% alpha wherever the two overlap, which is everywhere.
                }}
              >
                <Typography variant="body2" color="text.primary" fontWeight={600}>
                  {plan.title}
                </Typography>
                {/* No per-column "Current" chip. It lived here in a reserved-height
                    slot present in every column — necessary, because rendering it
                    conditionally dropped the current column's title off the shared
                    baseline. The chip now sits once, in the label column's header
                    cell beside the tier name, so the reserved slot and its baseline
                    problem both go away and every column's title is simply a title. */}
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  justifyContent="center"
                  spacing={2}
                  sx={{ mt: 3 }}
                >
                  <Typography variant="h3" color="text.primary">
                    {centsToDollar(plan.amount)}
                  </Typography>
                  <Typography variant="body3" color="text.secondary" sx={{ pb: 0.5 }}>
                    {capitalize(plan.interval?.toString() ?? "")}
                  </Typography>
                </Stack>
                {/* Always RENDERED, never conditional — it is the zero-height track
                    on monthly, not an absent element. That is what lets it animate in
                    BOTH directions; `{savingsLabel && …}` could only ever grow in,
                    because there was no node left to collapse on the way out.

                    Deliberately NOT a permanently reserved slot: the row still
                    occupies zero height when closed, so monthly is not padded out to
                    make annual cheaper. */}
                <SavingsRow label={savingsLabel} greenText={greenText} />
                {/* Optional per-column subline (VRG-383) — see `planSubline`.
                    Rendered in a FIXED, clamped box for the same alignment reason
                    the description below is: the sentence's length varies with the
                    plan's own numbers ("5 included activations" against "1,000"),
                    so an unclamped box would wrap to different heights per column
                    and take that column's CTA off the row's line.

                    Three lines rather than two: at the ~200px column width this
                    grid gives, the sentence is a three-line one. The row exists
                    only when the prop is passed, so the billing page's header
                    keeps its current height exactly. */}
                {planSubline && (
                  <Box
                    sx={{
                      // ONE line at body3's 14px and this slot's 1.5 line-height.
                      // Was 63px (three lines), sized for the long sentence this
                      // slot originally carried; now that the copy is three words
                      // that reservation would hold 42px of empty column open in
                      // the exact place the step was too crowded.
                      //
                      // Still a FIXED height rather than auto, for the original
                      // reason: a column rendering one line where its neighbour
                      // renders two pushes its CTA out of line with the rest of
                      // the row. The clamp below keeps longer copy from
                      // overflowing if this slot is ever reused.
                      height: 21,
                      mt: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& > *": {
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                        overflow: "hidden",
                      },
                    }}
                  >
                    {planSubline(plan)}
                  </Box>
                )}
                {/* FIXED two-line height, and the text is clamped to two lines.
                    Both halves are load-bearing.

                    A `minHeight` alone does not align the CTAs: measured, a one-line
                    description is 21px and a two-line one is 42px, so at minHeight 40
                    the two-line columns overflowed by 2px and pushed their buttons
                    below the others (511 vs 509).

                    The clamp is what keeps it true. `resolvePlanDescription` prefers the
                    REPO-owned line for the tier (VERA-1095 — see
                    `PLAN_TIER_DESCRIPTIONS`) but still falls back to the Stripe-authored
                    `plan.description` for a tier this repo does not know about, and that
                    fallback's length is set by whoever last edited the product in the
                    Stripe dashboard. A fixed height with unclamped text would break
                    alignment again the first time someone writes a three-line
                    description, with no failure anywhere to say so.

                    Resolved HERE rather than by each caller, which is what makes signup
                    and the billing plan-change page show the same string by construction
                    rather than by both remembering to. */}
                <Box sx={{ height: 42, mt: 3, display: "flex", alignItems: "center" }}>
                  {resolvePlanDescription(plan) && (
                    <Typography
                      variant="body3"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.5,
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                      }}
                    >
                      {resolvePlanDescription(plan)}
                    </Typography>
                  )}
                </Box>
                {/* `stopPropagation` keeps the button from double-firing WHEN the
                    column is a click target too (`columnClickable`, signup only):
                    without it a click here would run `action.onClick` on the button
                    AND again on the enclosing header cell as it bubbles — two
                    setStates. It is a no-op when the column is inert, which is the
                    billing default, so it stays unconditionally rather than being
                    wired to the same flag in a second place.

                    Stopped on the WRAPPER rather than inside the button's own
                    handler, so it also covers a click that lands in this box but
                    misses the button. The button is `fullWidth` and fills the
                    box, so in practice that is a hairline; stopping it here is
                    free and means the rule is "this box is the button's", not
                    "remember to stop it in every handler".

                    `onFocus`/`onBlur` are the keyboard half (see `focusedPlanId`):
                    React's focus events bubble, so tabbing INTO the button lights
                    its whole column, which is what tells a keyboard user which
                    column the CTA belongs to. */}
                <Box
                  sx={{ width: "100%", mt: 4 }}
                  onClick={(event) => event.stopPropagation()}
                  onFocus={() => setFocusedPlanId(plan.id)}
                  onBlur={() =>
                    setFocusedPlanId((current) =>
                      current === plan.id ? null : current,
                    )
                  }
                >
                  <Button
                    fullWidth
                    variant={action.variant}
                    disabled={action.disabled}
                    onClick={action.onClick}
                    /* Four columns of buttons all reading "Select" (or all
                       reading "Upgrade") are indistinguishable to a screen
                       reader, which is the actual reason "reach the column by
                       keyboard" did not work before. Naming the plan makes each
                       CTA identify its own column. */
                    aria-label={`${action.label} — ${plan.title}`}
                  >
                    {action.label}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Expand/collapse-all, directly above the first row rather than in the card's
            top-right corner where it started.

            Up there it read as card chrome and sat a long way from the thing it acts
            on — nothing told you the sections below were collapsible at all, which is
            the actual job of this control. Here it is the last thing before the table
            body, so it is read as an instruction about what follows.

            Left-aligned in the label column, because that is the column whose rows it
            expands; right-aligned it would sit over the plan columns, which it does
            not affect. */}
        {/* Lead reassurance row: the whole product is on every plan. */}
        {dataRow(CORE_FEATURES_ROW, "core", true)}

        {sections.map((section, sectionIndex) => {
          const isCollapsed = collapsed.has(section.title);
          // Which element actually ENDS the tinted band depends on collapse state:
          // the last section's final data row when it is open, its header row when it
          // is not. Computed rather than assumed, so the corner stays round through
          // every combination of expand/collapse.
          const isLastSection = sectionIndex === sections.length - 1;
          return (
            <React.Fragment key={section.title}>
              {/* Section header — grey band, clickable accordion, chevron. Plan cells
                  fill the row height (alignItems stretch) so the current-column purple
                  runs continuously through the header instead of breaking. */}
              {/* The accordion toggle moved from THIS ROW onto its label cell
                  (VERA-1095), so the plan cells in the same row can belong to
                  their columns instead.

                  It looks like a demotion and is not: the file's own note on the
                  cells below already establishes that the plan-column part of
                  this grey strip belongs to the COLUMN — the selected column
                  punches a hole through the grey rather than painting over it,
                  precisely so the column reads as continuous. Leaving the toggle
                  on the whole row would have meant the column's click target was
                  the one thing that ISN'T continuous, interrupted four times by
                  bands that do something else.

                  The toggle loses nothing findable: the chevron, the section
                  title and a 220px+ target are all in the label cell, which is
                  where anyone looks to open an accordion. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns,
                  alignItems: "stretch",
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box
                  onClick={() => toggleSection(section.title)}
                  sx={{
                    cursor: "pointer",
                    // Symmetric. This was pt: 4 / pb: 2 — 16 top against 8 bottom — so
                    // every section band sat low in its own row and the label read as
                    // closer to the rows BELOW it than to the band it belongs to.
                    py: 2,
                    pl: 3,
                    pr: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: theme.palette.primary[50],
                  }}
                >
                  {/* One rotating glyph, not a swap — see `chevronSx`. */}
                  <Box sx={{ display: "flex", ...chevronSx(isCollapsed) }}>
                    <ChevronDownIcon
                      size={16}
                      strokeWidth={2}
                      color={theme.palette.text.secondary}
                    />
                  </Box>
                  <Typography
                    variant="body4"
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>
                {plans.map((plan) => {
                  const { cursor, ...hit } = planCellProps(plan);
                  return (
                  <Box
                    key={`sec-${section.title}-${plan.id}`}
                    {...hit}
                    sx={{
                      cursor,
                      // The selected column punches a HOLE in the grey strip rather than
                      // painting tint over it: the band behind the table is what shows
                      // through, so the column stays one continuous colour through every
                      // section header instead of breaking.
                      //
                      // Deliberately NOT rounded, even when this row ends the table.
                      // Rounding it curved the GREY strip too, which reads as a notch cut
                      // out of a full-width band rather than as the end of a column — the
                      // grey runs edge to edge, so it has no business having corners
                      // partway across. The band's own rounded cap sits BELOW this row,
                      // clear of the grey, which is where a corner can read correctly.
                      //
                      // A HOVERED/FOCUSED column punches the same hole (VERA-1095),
                      // for the same reason: without it the hover tint would stop
                      // dead at every section header and the column's highlight
                      // would read as four disconnected fragments rather than as
                      // one column. Same mechanism, weaker tint.
                      backgroundColor:
                        isCurrentColumn(plan) || isActiveColumn(plan)
                          ? "transparent"
                          : theme.palette.primary[50],
                    }}
                  />
                  );
                })}
              </Box>

              {/* The section body, height-animated.

                  `grid-template-rows` 0fr -> 1fr is the mechanism: a grid track
                  sized in `fr` is animatable where `height: auto` is not, so the
                  wrapper grows to exactly the rows' height without anyone
                  measuring anything — no ResizeObserver, no rAF, no stateful
                  pixel height to keep in sync as rows change. The inner box MUST
                  carry `overflow: hidden` and `minHeight: 0`: a grid item's
                  automatic minimum size is content-based, so without them the
                  child refuses to shrink below its rows and the track never
                  collapses.

                  Not `@repo/ui`'s `useAnimatedHeight`, and not MUI `<Collapse>`:
                  the first is ResizeObserver-driven (and self-skips while
                  hidden, which is every collapsed section here); the second
                  measures too. This degrades to an instant-but-CORRECT layout
                  wherever transitions do not run, which is the property that
                  matters — the rows are rendered by `isCollapsed`, never by the
                  animation, so a section can never be stuck invisible.

                  The rows stay independent grids and are NOT re-parented into a
                  shared one, so every column still resolves its tracks from the
                  same `gridTemplateColumns` against the same width. The wrapper
                  is full-width and contributes no columns of its own.

                  The tint band needs no work to follow this: it is absolutely
                  positioned against the table box with `top`/`bottom`, and that
                  box is what this wrapper resizes, so the band interpolates with
                  the section instead of jumping to the new height.

                  Collapse-all moves all four of these AT ONCE, on the same
                  duration and curve with no per-section delay — deliberately.
                  Staggering would read as a ripple travelling down the table and
                  stretch a "tidy this away" action to 4 delays + 260ms; in
                  lockstep the table reads as one surface closing. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateRows: isCollapsed ? "0fr" : "1fr",
                  transition: `grid-template-rows ${SECTION_MOTION_MS}ms ${SECTION_EASING}`,
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
              >
                {/* `aria-hidden` is NOT decoration here — it is what keeps this
                    from being an accessibility regression.

                    Animating a height requires the rows to EXIST, so a collapsed
                    section no longer unmounts them the way `!isCollapsed && …`
                    did; it clips them to a zero-height track instead. Visually
                    identical, but without this every collapsed section would
                    still be read out in full by a screen reader, which is
                    exactly what collapsing it was meant to prevent.

                    `pointerEvents: none` is the other half: the label cells are
                    tooltip triggers, and a zero-height strip is still a hit
                    target for a mouse that happens to cross it.

                    `inert` would be the single correct answer to both — but it
                    is absent from `@types/react` 18 (checked: 18.3.26), so
                    passing it here is a type error rather than an improvement.
                    The pair below is equivalent for this content TODAY because
                    a collapsed section contains no focusable element at all
                    (measured: zero). That is the invariant to preserve — if a
                    row ever gains a link or a button, `aria-hidden` around a
                    focusable node becomes a genuine violation, and this needs
                    `inert` (and the JSX augmentation to type it) instead. */}
                <Box
                  aria-hidden={isCollapsed}
                  sx={{
                    overflow: "hidden",
                    minHeight: 0,
                    ...(isCollapsed ? { pointerEvents: "none" } : null),
                  }}
                >
                  {section.rows.map((row, rowIndex) =>
                    dataRow(
                      row,
                      section.title,
                      false,
                      isLastSection && rowIndex === section.rows.length - 1,
                    ),
                  )}
                </Box>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
      {footer}
    </Card>
  );
};
